import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { type GitHubIssuesPayload, parseGitHubIssuesPayload } from "./githubIssuesSchema";
import { GitHubIssueMeta, safeGitHubUrl } from "./githubRenderUtils";

interface GitHubIssuesRendererProps {
  block: StreamingBlock;
}

export const GitHubIssuesRenderer = (props: GitHubIssuesRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GitHubIssuesPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGitHubIssuesPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("github_issues_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().issues.length > 0 || data().capped || data().incomplete}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbBrandGithub class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">
                  <Show when={data().query} fallback={rt.t("github_issues_card.title")}>
                    {(query) => rt.t("github_issues_card.query_title", { query: query() })}
                  </Show>
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().issues.length === 1
                    ? rt.t("github_issues_card.results_one")
                    : rt.t("github_issues_card.results", { count: String(data().issues.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().issues}>
                  {(issue) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-baseline gap-1.5">
                          <span class="shrink-0 font-mono text-xs text-muted-text">{issue.reference}</span>
                          <Show
                            when={safeGitHubUrl(issue.url)}
                            fallback={<span class="truncate text-sm text-primary-text">{issue.title}</span>}
                          >
                            {(href) => (
                              <a
                                href={href()}
                                target="_blank"
                                rel="noopener noreferrer"
                                class="truncate text-sm text-brand hover:underline"
                              >
                                {issue.title}
                              </a>
                            )}
                          </Show>
                        </span>
                        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(issue.updatedAt)}</span>
                      </div>
                      <GitHubIssueMeta issue={issue} />
                    </li>
                  )}
                </For>
              </ul>

              <Show when={data().capped || data().incomplete}>
                <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                  <Show when={data().capped}>
                    <div>{rt.t("github_issues_card.capped", { total: String(data().totalCount) })}</div>
                  </Show>
                  <Show when={data().incomplete}>
                    <div>{rt.t("github_issues_card.incomplete")}</div>
                  </Show>
                </div>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
